import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Anexos from "../components/anexos";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

interface EncaminhamentoCirurgicoProps {
  onDataChange?: (data: any) => void;
  initialData?: any;
  atendimentoId?: number;
  pacienteId?: number;
  empresaId?: number;
}

export const EncaminhamentoCirurgico = ({ onDataChange, initialData, atendimentoId, pacienteId, empresaId }: EncaminhamentoCirurgicoProps) => {
  const [formData, setFormData] = useState({
    tipoCirurgia: initialData?.tipoCirurgia || "",
    olho: initialData?.olho || "",
    indicacao: initialData?.indicacao || "",
    cirurgiao: initialData?.cirurgiao || "",
    dataCirurgia: initialData?.dataCirurgia || "",
    horarioCirurgia: initialData?.horarioCirurgia || "",
    salaCirurgia: initialData?.salaCirurgia || "",
    anestesia: initialData?.anestesia || "",
    jejum: initialData?.jejum || "",
    medicamentosPreCirurgia: initialData?.medicamentosPreCirurgia || "",
    cuidadosPreCirurgia: initialData?.cuidadosPreCirurgia || "",
    observacoes: initialData?.observacoes || "",
  });

  const handleInputChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange?.(newData);
  };

  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);
  const [quantidadeExames, setQuantidadeExames] = useState<number>(10); // Limite padrão de 10 para Encaminhamento Cirúrgico

  // Sincronizar protocoloId quando initialData mudar
  useEffect(() => {
    if (initialData?.id) {
      setProtocoloId(initialData.id);
    }
  }, [initialData?.id]);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const currentAtendimentoId = atendimentoId || (initialData as any)?.id_atendimento;
      if (!currentAtendimentoId) {
        throw new Error('ID do atendimento não fornecido');
      }
      return await saveFormProtocolo({
        endpoint: '/api/protocolo-encaminhamento-cirurgico',
        data,
        atendimentoId: currentAtendimentoId,
        pacienteId: pacienteId || (initialData as any)?.id_paciente,
        empresaId: empresaId || (initialData as any)?.id_empresa,
        protocoloId: protocoloId || initialData?.id,
        formName: 'Encaminhamento Cirúrgico'
      });
    },
    onSuccess: (response) => {
      // Extrair ID do response (pode estar em response.result.id ou response.data.result.id)
      const savedId = response?.result?.id || response?.data?.result?.id || protocoloId || initialData?.id;
      if (savedId && savedId !== protocoloId) {
        setProtocoloId(savedId);
        // Atualizar initialData através do callback
        onDataChange?.({ ...formData, id: savedId } as any);
      }
      
      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar encaminhamento cirúrgico:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleSave = useCallback(async () => {
    const currentAtendimentoId = atendimentoId || (initialData as any)?.id_atendimento;
    if (!currentAtendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      console.error('AtendimentoId não encontrado:', { atendimentoId, initialData });
      return;
    }

    // Definir campos obrigatórios para Encaminhamento Cirúrgico
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(formData, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando encaminhamento cirúrgico:', { 
        endpoint: '/api/protocolo-encaminhamento-cirurgico', 
        data: formData, 
        atendimentoId: currentAtendimentoId,
        pacienteId: pacienteId || (initialData as any)?.id_paciente,
        empresaId: empresaId || (initialData as any)?.id_empresa
      });
      await saveMutation.mutateAsync(formData);
      console.log('Encaminhamento cirúrgico salvo com sucesso');
    } catch (error) {
      console.error('Erro ao salvar encaminhamento cirúrgico:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, initialData, formData, saveMutation, pacienteId, empresaId]);

  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-encaminhamento-cirurgico/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "encaminhamento_cirurgico",
        fallbackUrl: url
      });
      if (!blob.size) {
        throw new Error('PDF vazio recebido do servidor');
      }
      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao visualizar PDF:', error);
      toast.error(error?.message || 'Erro ao gerar PDF');
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };


  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending
            ? (protocoloId ? 'Atualizando...' : 'Salvando...')
            : (protocoloId ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
          title={!protocoloId && !initialData?.id ? "Salve o formulário antes de visualizar" : "Visualizar PDF"}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tipo de Cirurgia</Label>
          <Input 
            placeholder="Ex: Catarata, Glaucoma, Retina..." 
            value={formData.tipoCirurgia}
            onChange={(e) => handleInputChange('tipoCirurgia', e.target.value)}
          />
        </div>
        <div>
          <Label>Olho</Label>
          <Input 
            placeholder="Ex: OD, OE, Ambos" 
            value={formData.olho}
            onChange={(e) => handleInputChange('olho', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Indicação Cirúrgica</Label>
        <Textarea 
          placeholder="Descreva a indicação para a cirurgia..." 
          rows={3} 
          value={formData.indicacao}
          onChange={(e) => handleInputChange('indicacao', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Cirurgião Responsável</Label>
          <Input 
            placeholder="Nome do cirurgião" 
            value={formData.cirurgiao}
            onChange={(e) => handleInputChange('cirurgiao', e.target.value)}
          />
        </div>
        <div>
          <Label>Data da Cirurgia</Label>
          <Input 
            type="date"
            value={formData.dataCirurgia}
            onChange={(e) => handleInputChange('dataCirurgia', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Horário da Cirurgia</Label>
          <Input 
            type="time"
            value={formData.horarioCirurgia}
            onChange={(e) => handleInputChange('horarioCirurgia', e.target.value)}
          />
        </div>
        <div>
          <Label>Sala Cirúrgica</Label>
          <Input 
            placeholder="Ex: Sala 1, Sala 2..." 
            value={formData.salaCirurgia}
            onChange={(e) => handleInputChange('salaCirurgia', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Tipo de Anestesia</Label>
        <Input 
          placeholder="Ex: Local, Tópica, Geral..." 
          value={formData.anestesia}
          onChange={(e) => handleInputChange('anestesia', e.target.value)}
        />
      </div>

      <div>
        <Label>Orientações de Jejum</Label>
        <Textarea 
          placeholder="Orientações sobre jejum..." 
          rows={2} 
          value={formData.jejum}
          onChange={(e) => handleInputChange('jejum', e.target.value)}
        />
      </div>

      <div>
        <Label>Medicamentos Pré-Cirúrgicos</Label>
        <Textarea 
          placeholder="Medicamentos a serem administrados antes da cirurgia..." 
          rows={2} 
          value={formData.medicamentosPreCirurgia}
          onChange={(e) => handleInputChange('medicamentosPreCirurgia', e.target.value)}
        />
      </div>

      <div>
        <Label>Cuidados Pré-Cirúrgicos</Label>
        <Textarea 
          placeholder="Cuidados especiais antes da cirurgia..." 
          rows={3} 
          value={formData.cuidadosPreCirurgia}
          onChange={(e) => handleInputChange('cuidadosPreCirurgia', e.target.value)}
        />
      </div>

      <div>
        <Label>Observações</Label>
        <Textarea 
          placeholder="Observações adicionais..." 
          rows={3} 
          value={formData.observacoes}
          onChange={(e) => handleInputChange('observacoes', e.target.value)}
        />
      </div>

      <div className="mt-6 pt-6 border-t">
        <Anexos 
          protocoloId={protocoloId || initialData?.id || (formData as any)?.id}
          quantidadeExames={quantidadeExames}
          endpointBase="/api/protocolo-encaminhamento-cirurgico"
        />
      </div>
      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`encaminhamento_cirurgico_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default EncaminhamentoCirurgico;
